import React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useNavigate, useParams } from 'react-router-dom';
import useDanteApi from '@/hooks/useDanteApi';
import * as voiceService from '@/services/voice.service';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import ChevronDownIcon from '@/shared/icons/ChevronDownIcon';
import VoiceIcon from '@/shared/icons/VoiceIcon';
import RightChevronIcon from '@/shared/icons/RightChevronIcon';
import LeftArrowIcon from '@/shared/icons/LeftArrowIcon';
import DSpinner from '@/components/Global/DSpinner';

const VoiceAgentDropdown = ({ isMobileView = false, isMobileButton = false, onMobileButtonClick, onBack, onClose }) => {
  const navigate = useNavigate();
  const params = useParams();

  // Fetch all voices
  const { data: voicesData, loading: voicesLoading } = useDanteApi(
    voiceService.getVoices,
    []
  );
  const voices = voicesData?.results || [];

  // Fetch current voice data if on voice route
  const { data: currentVoiceData, loading: currentVoiceLoading } = useDanteApi(
    voiceService.getVoice,
    [params.id],
    { skip: !params.id },
    params.id
  );

  const currentVoice = currentVoiceData || voices.find(v => v.id === params.id);
  const isLoading = currentVoiceLoading || (voicesLoading && !currentVoice);

  // Shared MenuItems component
  const VoicesMenuItems = ({ position = "right", close }) => {
    const anchorPosition = position === "right" ? "bottom end" : "bottom start";

    const handleCreateVoice = () => {
      navigate('/voice/create');
      close?.();
    };

    const handleVoiceClick = (voiceId) => {
      navigate(`/voice/${voiceId}`);
      close?.();
    };

    // Filter out the current voice from the list
    const otherVoices = voices.filter(voice => voice.id !== currentVoice?.id);

    return (
      <MenuItems
        anchor={anchorPosition}
        className="w-[400px] origin-top-right bg-white border border-v3-card-border shadow-v3-card rounded-v3-lg overflow-y-auto z-50 focus:outline-none transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] data-[closed]:scale-95 data-[closed]:opacity-0 mt-v3-sm"
      >
        {/* Current Voice Header */}
        <div className='bg-v3-black-lightest border border-v3-card-border rounded-v3-lg px-v3-sm py-v3-xs'>
          <div className='flex items-center justify-between pb-v3-xs'>
            {isLoading ? (
              <DSpinner style={{ height: 20, width: 20 }} />
            ) : (
              <p className='text-v3-black-max text-v3-h9 font-medium'>{currentVoice?.name || 'Voice Agent'}</p>
            )}
          </div>
        </div>

        {/* Other Voices List */}
        {otherVoices.length > 0 ? (
          <div className='max-h-[200px] overflow-y-auto'>
            {otherVoices.map((voice) => (
              <MenuItem key={voice.id} as="div">
                {() => (
                  <button
                    onClick={() => handleVoiceClick(voice.id)}
                    className={`flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm rounded-v3-lg transition-colors w-full hover:bg-v3-menu-hover h-[48px]`}
                  >
                    <span className='text-sm font-medium truncate text-v3-black-max'>
                      {voice.name}
                    </span>
                  </button>
                )}
              </MenuItem>
            ))}
          </div>
        ) : null}
        
        {/* Create New Voice */}
        <div className='p-v3-sm'>
          <button
            onClick={handleCreateVoice}
            className='w-full text-center text-v3-purple-primary text-v3-h10 font-medium hover:opacity-80 transition-opacity py-[10px]'
          >
            + Create New Voice Agent
          </button>
        </div>
      </MenuItems>
    );
  };

  // Mobile button only (for main nav view)
  if (isMobileView && isMobileButton) {
    return (
      <button
        onClick={onMobileButtonClick}
        className="flex items-center justify-between w-full text-v3-h10 py-[8px]  font-regular rounded transition-colors hover:bg-v3-menu-hover cursor-pointer"
      >
        <div className="flex items-center gap-v3-xs">
          <IconWrapper size={20}>
            <VoiceIcon className="w-full h-full" />
          </IconWrapper>
          {isLoading ? (
            <DSpinner style={{ height: 16, width: 16 }} />
          ) : (
            <p className="text-v3-h10 font-medium text-v3-black-max">
              {currentVoice?.name || 'Voice Agent'}
            </p>
          )}
        </div>
        <IconWrapper size={20}>
          <RightChevronIcon className="w-full h-full text-v3-purple-primary" />
        </IconWrapper>
      </button>
    );
  }

  // Mobile full-page view
  if (isMobileView) {
    const otherVoices = voices.filter(voice => voice.id !== currentVoice?.id);

    return (
      <div className="md:hidden w-full h-full flex flex-col overflow-y-auto bg-white md:px-v3-lg px-v3-sm">
        {/* Back Button */}
        <div className="h-[40px] my-[12px]">
          <button onClick={onBack} className="flex items-center gap-v3-xs text-v3-purple-primary py-[10px]">
            <IconWrapper size={20}>
              <LeftArrowIcon className="w-full h-full text-v3-purple-primary" />
            </IconWrapper>
            <span className="text-v3-h10 font-medium">Back</span>
          </button>
        </div>

        {/* Voice Info */}
        <div className="flex-1 flex flex-col">
          {/* Selected Voice Header */}
          <div className="bg-v3-black-lightest border border-v3-card-border rounded-v3-lg px-v3-sm py-[12px]">
            <div className="flex items-center justify-between pb-v3-sm">
              <div className="flex items-center gap-v3-xs">
                <IconWrapper size={20}>
                  <VoiceIcon className="w-full h-full" />
                </IconWrapper>
                {isLoading ? (
                  <DSpinner style={{ height: 20, width: 20 }} />
                ) : (
                  <p className="text-v3-black-max text-v3-h9 font-medium">{currentVoice?.name || 'Voice Agent'}</p>
                )}
              </div>
            </div>
          </div>

          {/* All Voices List */}
          {otherVoices.length > 0 ? (
            <div className="flex flex-col max-h-[500px] overflow-y-auto">
              {otherVoices.map((voice) => (
                <button
                  key={voice.id}
                  onClick={() => {
                    navigate(`/voice/${voice.id}`);
                    onClose?.();
                  }}
                  className="flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm rounded-v3-lg transition-colors w-full hover:bg-v3-menu-hover text-left h-[48px]"
                >
                  <span className="text-sm font-medium truncate text-v3-black-max">
                    {voice.name}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {/* Create New Voice Agent */}
          <div className="px-v3-lg py-v3-md text-center">
            <button
              onClick={() => {
                navigate('/voice/create');
                onClose?.();
              }}
              className="text-sm font-medium text-v3-purple-primary hover:opacity-80 transition-opacity"
            >
              + Create New Voice Agent
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop dropdown view (per-user mode)
  return (
    <div className="flex items-center gap-v3-xs whitespace-nowrap">
      <IconWrapper size={20}>
        <VoiceIcon className="w-full h-full text-v3-black-placeholder " />
      </IconWrapper>
      {isLoading ? (
        <DSpinner style={{ height: 16, width: 16 }} />
      ) : (
        <p className="text-v3-h10 font-medium text-v3-black-max">{currentVoice?.name || 'Voice Agent'}</p>
      )}

      <Menu className="h-5">
        {({ open, close }) => (
          <div className="relative">
            <MenuButton className="text-black hover:bg-v3-menu-hover rounded-v3-xxs transition-colors relative h-5">
              <IconWrapper size={20}>
                <ChevronDownIcon className={`w-full h-full text-v3-purple-primary hover:text-v3-purple-medium transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
              </IconWrapper>
            </MenuButton>
            <VoicesMenuItems position="right" close={close} />
          </div>
        )}
      </Menu>
    </div>
  );
};

export default VoiceAgentDropdown;


